import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SunnyHoursData {
  fromTime: string;
  toTime: string;
  frequency: number;
}

interface TypicalSunnyHours {
  mostCommonHours: SunnyHoursData | null;
  totalReports: number;
  recentReports: number; // Reports from last 30 days
  loading: boolean;
  error: string | null;
}

export const useTypicalSunnyHours = (terraceId: string): TypicalSunnyHours => {
  const [data, setData] = useState<TypicalSunnyHours>({
    mostCommonHours: null,
    totalReports: 0,
    recentReports: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    const fetchSunnyHours = async () => {
      if (!terraceId) return;

      try {
        setData(prev => ({ ...prev, loading: true, error: null }));

        // Fetch all sunny hours reports for this terrace
        const { data: reports, error } = await supabase
          .from('visitor_checkins')
          .select('content, submitted_at')
          .eq('terrace_id', terraceId)
          .eq('feedback_type', 'sunny_hours')
          .eq('is_approved', true)
          .order('submitted_at', { ascending: false });

        if (error) throw error;

        if (!reports || reports.length === 0) {
          setData(prev => ({ 
            ...prev, 
            loading: false,
            totalReports: 0,
            recentReports: 0,
            mostCommonHours: null
          }));
          return;
        }

        // Count recent reports (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const recentReports = reports.filter(report => 
          new Date(report.submitted_at) >= thirtyDaysAgo
        ).length;

        // Aggregate time ranges to find most common
        const timeRangeFrequency: { [key: string]: number } = {};
        
        reports.forEach(report => {
          const content = report.content as { from_time: string; to_time: string; date: string };
          if (content.from_time && content.to_time) {
            const key = `${content.from_time}-${content.to_time}`;
            timeRangeFrequency[key] = (timeRangeFrequency[key] || 0) + 1;
          }
        });

        // Find most common time range
        let mostCommonHours: SunnyHoursData | null = null;
        let maxFrequency = 0;

        Object.entries(timeRangeFrequency).forEach(([timeRange, frequency]) => {
          if (frequency > maxFrequency) {
            maxFrequency = frequency;
            const [fromTime, toTime] = timeRange.split('-');
            mostCommonHours = {
              fromTime,
              toTime,
              frequency
            };
          }
        });

        setData({
          mostCommonHours,
          totalReports: reports.length,
          recentReports,
          loading: false,
          error: null
        });

      } catch (error) {
        console.error('Error fetching sunny hours:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load sunny hours data'
        }));
      }
    };

    fetchSunnyHours();
  }, [terraceId]);

  return data;
};